import { Col, Form, Row, Select } from "antd";
import Table from "atoms/Table";
import Title from "atoms/Title";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import queryKeys from "common/queryKey";
import { useScrollFetch } from "hooks/useScrollFetch";
import { uniqBy } from "lodash";
import moment from "moment";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { setFilter } from "redux/actions";
import {
  getClientid,
  getFilters,
  getManagingOffice,
  getUserInfo,
  getUserList,
} from "redux/selectors";
import { ButtonOutlined } from "reusableComponent/UIButtons";
import styled from "styled-components";
import { utils, writeFileXLSX } from "xlsx";

const RoundedSelect = styled(Select)`
  &:where(.ant-select) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select-focused) .ant-select-selector {
    border-radius: 15px !important;
  }

  &:where(.ant-select) .ant-select-selector:hover {
    border-radius: 15px !important;
  }
`;

function OnboardingReport() {
  const userList = useSelector((state) => getUserList(state));
  const [payload, setPayload] = useState({});
  const clientId = useSelector(getClientid);
  const managingOfficeList = useSelector(getManagingOffice);
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(0)

  const isSuperAdmin = userInfo?.roleId === "2";

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
      className: "normal-column ",
      render: (val) => {
        return <span>{val}</span>;
      },
    },
    {
      title: "Email Id",
      dataIndex: "userid",
      key: "userid",
      className: "normal-column",
      render: (val, record) => {
        return <span>{val ?? "-"}</span>;
      },
    },

    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      className: "normal-column",
      ellipsis: {
        showTitle: true,
      },
    },
    {
      title: "Submission Status",
      dataIndex: "onBoardingFormStatus",
      key: "onBoardingFormStatus",
      className: "normal-column",
      render: (val, record) => {
        return <span>{val === "0" ? "Yes" : "No"}</span>;
      },
    },
    {
      title: "Country",
      dataIndex: "managingoffice",
      key: "managingoffice",
      className: "normal-column",
      render: (val, record) => {
        return <span>{val?.[0]}</span>;
      },
    },
    {
      title: "Date Submitted",
      dataIndex: "onboardingdate",
      key: "onboardingdate",
      className: "normal-column",
      render: (val) => (val ? moment(val).format("DD-MM-YYYY") : "-"),
    },
  ];

  const [form] = Form.useForm();

  const filters = useSelector(getFilters);
  const menuName = "onboardingReport";
  const initialFilter = filters[menuName];

  const dispatch = useDispatch();

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    dispatch(setFilter(menuName, "name", values.name || []));
    dispatch(setFilter(menuName, "status", values.status || []));
  };

  const handleCountryChange = (value) => {
    dispatch(setFilter(menuName, "managingoffice", value));
  };

  const { getOnboardingReport } = queryKeys;

  const selectedManagingOffice = isSuperAdmin
    ? filters?.onboardingReport?.managingoffice ||
    finduser?.managingoffice?.[0] ||
    managingOfficeList?.[0]
    : finduser?.managingoffice?.[0];

  const isInitialMount = useRef(true);
  const previousCountry = useRef(selectedManagingOffice);

  const { key, url } = getOnboardingReport(clientId, selectedManagingOffice);

  const {
    data,
    lastElementRef,
    isLoading,
    refetch,
    isReloading,
    fetchNextPage,
  } = useScrollFetch(key, {
    endPoint: url,
    queryParam: "LastEvaluatedKey",
    extra: {
      clientId: clientId,
      country: selectedManagingOffice,

    },
    enabled: !!lastEvaluatedKey,
  });

  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousCountry.current = selectedManagingOffice;
      return;
    }

    // Only refetch if country actually changed
    if (previousCountry.current !== selectedManagingOffice) {
      previousCountry.current = selectedManagingOffice;
      refetch();
    }
  }, [selectedManagingOffice, refetch]);

  useEffect(() => {
    if (
      isSuperAdmin &&
      !filters?.onboardingReport?.managingoffice &&
      finduser?.managingoffice?.[0]
    ) {
      dispatch(
        setFilter(menuName, "managingoffice", finduser.managingoffice[0])
      );

      form.setFieldsValue({
        managingoffice: finduser.managingoffice[0],
      });
    }
  }, [isSuperAdmin, finduser]);



  const dataList = useMemo(() => {
    setLastEvaluatedKey(data?.pages?.[data.pages.length - 1]?.response?.LastEvaluatedKey);
    if (data) {
      return data?.pages?.reduce((acc, page) => {
        if (page?.response === undefined) return acc;
        return [...acc, ...page?.response];
      }, []);
    } else {
      return [];
    }
  }, [data]);

  const userOptions = dataList?.map((user) => ({
    label: user?.fullname,
    value: user?.userid,
  }));

  const managingOfficeOption = managingOfficeList?.map((val) => {
    return {
      label: val,
      value: val,
    };
  });

  const statusOptions = [
    {
      label: "Submitted",
      value: "yes",
    },
    {
      label: "Not Submitted",
      value: "no",
    },
  ];

  const getFilteredData = () => {
    const name = form.getFieldValue("name") || [];
    const status = form.getFieldValue("status") || [];

    return dataList?.filter((item) => {
      const nameMatch = name.length > 0 ? name.includes(item.userid) : true;

      const statusMatch =
        status.length > 0
          ? status.includes(item?.onBoardingFormStatus === "0" ? "yes" : "no")
          : true;

      return nameMatch && statusMatch;
    });
  };

  const xport = React.useCallback(() => {
    const elt = document.getElementById("Table");
    const wb = utils.table_to_book(elt);
    writeFileXLSX(wb, "Onboarding Report.csv");
  });



  return (
    <div>
      <Row>
        <Col sm={24}>
          <div className="d-flex justify-content-between">
            <Title>Onboarding Report</Title>
            <ButtonOutlined
              className="text-end"
              disabled={
                getFilteredData().length === 0 || null || undefined
              }
              onClick={xport}
            >
              Export CSV file
            </ButtonOutlined>
          </div>
        </Col>
        <Col sm={24}>
          <Form
            form={form}
            layout="vertical"
            className="mt-2"
            onValuesChange={handleFormChange}
            initialValues={{ ...initialFilter }}
          >
            <Row gutter={12} className="g-3 mb-2">
              <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                <DropdownCheckBox
                  name="name"
                  label="Name"
                  options={userOptions}
                  form={form}
                  handleFormChange={handleFormChange}
                />
              </Col>
              {isSuperAdmin && (
                <Col xs={24} sm={24} md={12} lg={6} xl={5}>
                  <Form.Item name="managingoffice">
                    <RoundedSelect
                      placeholder="Select Country"
                      options={managingOfficeOption}
                      onChange={handleCountryChange}
                      clearIcon={null}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        </Col>
      </Row>
      <Table
        dataSource={getFilteredData() ?? []}
        columns={columns}
        pagination={false}
        loading={isLoading}
        onRow={(record, index) => ({
          ref: index === dataList?.length - 20 ? lastElementRef : null,
        })}
        id="Table"
      />
    </div>
  );
}

export default OnboardingReport;